import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { productSlug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
  });

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${productSlug}`);
        setProduct(response.data);
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productSlug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.email) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        product_id: product.id,
        product_name: product.name,
        ...formData,
      });
      setSubmitted(true);
      toast.success("Inquiry submitted successfully! We'll be in touch soon.");
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-outfit text-2xl text-white mb-4">Product not found</h1>
          <Link to="/shop" className="text-[#D4AF37] hover:underline">
            Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <section className="py-6 px-4 sm:px-6 lg:px-8 bg-[#141414] border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <Link 
            to={`/shop`}
            className="inline-flex items-center text-gray-400 hover:text-[#D4AF37] transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Shop
          </Link>
        </div>
      </section>

      {/* Product Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Product Image */}
            <div className="animate-fade-in">
              <div className="aspect-square overflow-hidden rounded-sm border border-white/10">
                <img
                  src={product.image || "https://images.unsplash.com/photo-1633415565464-e1d69c3fd906?w=800"}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Product Info & Form */}
            <div className="animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <span className="text-[#D4AF37] text-sm uppercase tracking-wider">
                {product.category_name}
              </span>
              <h1 className="font-outfit text-3xl sm:text-4xl text-white mt-2 mb-4">
                {product.name}
              </h1>
              <p className="text-gray-400 mb-8 leading-relaxed">
                {product.description || "High-quality printing service with premium materials and professional finish."}
              </p>

              {/* Inquiry Form */}
              <div className="bg-[#141414] p-8 rounded-sm border border-white/10">
                <h2 className="font-outfit text-xl text-white mb-6">
                  {submitted ? "Thank You!" : "Interested? Get in Touch"}
                </h2>

                {submitted ? (
                  <div className="text-center py-6">
                    <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
                      <Check size={32} className="text-[#D4AF37]" />
                    </div>
                    <p className="text-gray-300 mb-4">
                      We've received your inquiry and will contact you shortly.
                    </p>
                    <Link 
                      to="/shop" 
                      className="text-[#D4AF37] hover:underline"
                    >
                      Continue Shopping
                    </Link>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <Label htmlFor="name" className="text-gray-300 mb-2 block">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="input-dark w-full"
                        placeholder="Enter your name"
                        data-testid="inquiry-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-gray-300 mb-2 block">
                        Phone Number *
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="input-dark w-full"
                        placeholder="Enter your phone number"
                        data-testid="inquiry-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-gray-300 mb-2 block">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="input-dark w-full"
                        placeholder="Enter your email"
                        data-testid="inquiry-email"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full btn-gold"
                      data-testid="submit-inquiry-btn"
                    >
                      {submitting ? "Submitting..." : "Submit Inquiry"}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
